/**
 * The wardrobe / dressing panel (Cosmetic Progression Slice E, ADR-0050 /
 * ADR-0044).
 *
 * A DOM panel - opened by a button near the HUD or the `/dress` chat shortcut - that
 * lets a player re-dress from the cosmetics they have earned. Like the splash and
 * chat overlays, this module owns **only** the DOM wiring; the markup and styling
 * live in `index.html`, and it reads no Pixi and no socket. It is therefore
 * unit-testable headlessly (jsdom), exactly like `chat.ts`/`splash.ts`.
 *
 * The collection is presented **grouped**, never as a flat grid, so it scales to
 * hundreds of owned pairs: **layer** (Hair, Beard, Shirt, …) -> owned **item types**
 * (a count badge, "Long Hair ×12") -> the item's **unlocked hue swatches**. Dressing
 * is "item then hue": expand an item, click a hue to wear that pair. An already-worn
 * item's hues are shown and its header doubles as the unequip control - clicking a
 * worn item bares that layer (the spec's "click an equipped item again to unequip").
 *
 * The server is authoritative: every equip/unequip is reported through `onEquip` as
 * the **whole desired outfit**, which `main.ts` forwards as an `EquipMessage`; the
 * worn result comes back on the snapshot stream and is reconciled here via
 * {@link WardrobeHandle.setOutfit}. The panel updates its **own** Pixi preview
 * optimistically through `onPreview` (the live look the player is assembling) so
 * dressing feels immediate despite the round-trip - the preview reuses the real
 * `PlayerSprite` stack (`splashPreview.ts`) in `main.ts`, so it can't drift from
 * what spawns.
 */
import { content } from './sim/index';
import type { WardrobePair } from './net/protocol';
import { createUiWindow } from './ui/window';
import { createPanelButton, createPromptCard } from './ui/prompt';

const { equipment } = content;

/**
 * The render-content the wardrobe preview is driven from, structurally a
 * `PlayerLook` (kept local so this DOM module never imports the Pixi render layer).
 * `main.ts` maps it straight onto `SplashPreview.update`.
 */
export interface WardrobeLook {
  /** UO body id (400/401) the preview assembles for. */
  body: number;
  /** 1-based UO skin hue applied to the bare body. */
  bodyHue: number;
  /** The worn cosmetics being previewed, as `{id, hue}` layers. */
  equipment: { id: string; hue: number }[];
}

/** Options for {@link initWardrobe}; all but `onEquip` optional so tests can stub. */
export interface WardrobeOptions {
  /**
   * Report the **whole desired worn outfit** whenever the player equips or unequips
   * (an empty list bares the body). `main.ts` forwards it to the server as the
   * authoritative {@link import('./net/protocol').EquipMessage}.
   */
  onEquip: (outfit: WardrobePair[]) => void;
  /**
   * Report the **auto-equip preference** whenever the player toggles the top-of-panel
   * checkbox (auto-equip toggle, ADR-0123). `main.ts` forwards it to the server as a
   * {@link import('./net/protocol').SetAutoEquipMessage}. Optional so a headless test
   * can omit it; the checkbox still tracks its own state when unwired.
   */
  onAutoEquip?: (enabled: boolean) => void;
  onUnlockFavorite?: () => void;
  /** Fired after the server confirms that a new favorite slot was purchased. */
  onFavoriteUnlocked?: () => void;
  /** Render one assembled-character preview for a saved outfit. */
  renderFavoritePreview?: (mount: HTMLElement, look: WardrobeLook) => void;
  onSaveFavorite?: (slot: number, outfit: WardrobePair[]) => void;
  /**
   * Drive the live preview with the look the player is currently assembling (fired
   * on open and on every equip/unequip). Optional so a headless test can omit it.
   */
  onPreview?: (look: WardrobeLook) => void;
  /** Called when the panel opens (e.g. so `main.ts` mounts the Pixi preview canvas). */
  onOpen?: () => void;
  /** Called when the panel closes (e.g. so `main.ts` tears the preview down). */
  onClose?: () => void;
  /**
   * Called whenever the panel's open-state flips, so the shared toolbar coordinator
   * (`toolbar.ts`) can enforce one-panel-at-a-time. Fires alongside `onOpen`/`onClose`
   * (those stay preview-specific). Optional for tests.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Map a 1-based UO hue id to a CSS colour for a swatch button (the real recolour
   * is the baked ramp; this is the same approximate preview chat/splash swatches
   * use). Defaults to a flat grey so headless tests need no palette.
   */
  hueCss?: (hue: number) => string;
  /** Map a hue id to its complete UO dark-to-light ramp for dye swatches. */
  hueGradientCss?: (hue: number) => string;
  /**
   * Source of randomness for the "Random" outfit control ([0, 1)); defaults to
   * `Math.random`. Injected by tests so the roll is deterministic.
   */
  random?: () => number;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts` drives the wardrobe through. */
export interface WardrobeHandle {
  /** Open the panel (idempotent); fires `onOpen` and seeds the preview. */
  open(): void;
  /** Close the panel (idempotent); fires `onClose`. */
  close(): void;
  /** Toggle the panel open/closed (what the button and `/dress` call). */
  toggle(): void;
  /** Whether the panel is currently open. */
  isOpen(): boolean;
  /** Replace the owned collection (from a server `Collection` push); re-renders if open. */
  setCollection(unlocked: readonly WardrobePair[]): void;
  /**
   * Reconcile the worn outfit to the authoritative snapshot (from `main.ts`'s own
   * entity). Re-renders the worn markers and preview only when the outfit actually
   * changed, so a 10Hz snapshot stream (ADR-0163) never thrashes the panel or fights a click.
   * While an optimistic equip is in flight, the in-flight frame still carrying the
   * pre-equip look is suppressed, so the panel never flickers back to the old look
   * under latency; every other authoritative outfit - the confirming echo or a
   * server-side divergence - is adopted. That suppression is bounded: if our Equip
   * is silently dropped (rate limiter) the server keeps broadcasting the pre-equip
   * look, so suppression gives up after a cap and reconciles to authoritative, so
   * the panel can never get permanently stuck.
   */
  setOutfit(outfit: readonly WardrobePair[]): void;
  /** Set the character's body + skin hue (from the login identity), for grouping/preview. */
  setBody(body: number, bodyHue: number): void;
  /**
   * Reflect the server's authoritative **auto-equip preference** into the checkbox
   * (auto-equip toggle, ADR-0123), from the login `AutoEquipState` push. Sets the
   * box **without** firing `onAutoEquip`, so restoring a saved choice never echoes
   * back to the server as a fresh toggle.
   */
  setAutoEquip(enabled: boolean): void;
  setFavorites(reserved: boolean, favorites: readonly (readonly WardrobePair[] | null)[]): void;
  setGold(gold: number): void;
}

/** Stable `item hue` key for outfit/collection comparisons. */
function pairKey(pair: WardrobePair): string {
  return `${pair.item} ${pair.hue}`;
}

/** Canonical layer order (the order layers first appear in the equipment registry). */
const LAYER_ORDER: string[] = [...new Set(equipment.all().map((def) => def.layer))];

/** Title-case one dotted token, e.g. `long` -> `Long`. */
function titleCase(token: string): string {
  return token ? token[0]!.toUpperCase() + token.slice(1) : token;
}

/**
 * A human label for an equipment id, derived from its `<noun>.<style>` shape:
 * `hair.long` -> "Long Hair", `mask.bear` -> "Bear Mask", `kilt.plain` -> "Plain
 * Kilt". The noun comes from the id (not the render layer) so `kilt`/`skirt` keep
 * their own word; the style leads, reading like natural English.
 *
 * Exported so the unlock notifications feed (`notifications.ts`, ADR-0124) labels an
 * unlocked pair exactly as the wardrobe does, off the one helper - the two can never
 * drift on how an item id reads.
 */
export function itemLabel(id: string): string {
  const [noun, style] = id.split('.');
  return style ? `${titleCase(style)} ${titleCase(noun!)}` : titleCase(noun ?? id);
}

/** The display name for a render layer, e.g. `hair` -> "Hair". Exported for {@link itemLabel}'s reuse note. */
export function layerLabel(layer: string): string {
  return titleCase(layer);
}

export function initWardrobe(opts: WardrobeOptions): WardrobeHandle {
  const doc = opts.doc ?? document;
  const hueCss = opts.hueCss ?? (() => '#888888');
  const hueGradientCss = opts.hueGradientCss ?? hueCss;
  const rand = opts.random ?? Math.random;
  const panel = doc.getElementById('wardrobe')!;
  const button = doc.getElementById('wardrobe-button');
  const closeBtn = doc.getElementById('wardrobe-close');
  const body = doc.getElementById('wardrobe-body')!;
  // The auto-equip toggle (ADR-0123). Optional in the DOM so a headless test that
  // mounts only the body still works; when present, its change fires `onAutoEquip`.
  const autoEquipBox = doc.getElementById('wardrobe-autoequip-checkbox') as HTMLInputElement | null;
  // The outfit-action controls (ADR-0125): randomize / clear the worn outfit. Both
  // optional in the DOM so a body-only headless mount still works; both commit through
  // the same `onEquip` path as the item headers, so the server stays authoritative.
  const randomBtn = doc.getElementById('wardrobe-random');
  const clearBtn = doc.getElementById('wardrobe-clear');
  const favoritesEl = doc.getElementById('wardrobe-favorites');
  // The hue filter bar (ADR-0244): a row of owned-hue swatches above the scrolling
  // list that narrows the collection to a single colour. Optional in the DOM so a
  // body-only headless mount still works; absent, the collection just shows unfiltered.
  const filterBar = doc.getElementById('wardrobe-filter');
  const layersColumn = doc.getElementById('wardrobe-layers');
  const itemsColumn = doc.getElementById('wardrobe-items');
  const huesColumn = doc.getElementById('wardrobe-hues');
  const huesListView = doc.getElementById('wardrobe-hues-list-view');
  const huesGridView = doc.getElementById('wardrobe-hues-grid-view');
  const searchInput = doc.getElementById('wardrobe-hue-search') as HTMLInputElement | null;
  const filterSelect = doc.getElementById('wardrobe-hue-filter');
  const filterOptions = doc.getElementById('wardrobe-hue-filter-options');
  const filterMenu = doc.getElementById('wardrobe-hue-filter-menu') as HTMLDetailsElement | null;

  let collection: WardrobePair[] = [];
  let outfit: WardrobePair[] = [];
  // While an optimistic equip is in flight, `pendingKey` is the sorted key of the
  // outfit we committed and `staleKey` is the sorted key of the *pre-equip* outfit.
  // Until the server echoes, the only authoritative reconcile worth ignoring is the
  // in-flight frame still carrying the pre-equip look (incoming === staleKey); every
  // other outfit - the confirming echo or a server-side divergence - is adopted and
  // releases the suppression, so the panel can never get stuck (see setOutfit).
  let pendingKey: string | null = null;
  let staleKey: string | null = null;
  // How many in-flight pre-equip frames we have suppressed since the optimistic
  // commit. If our Equip is silently dropped (e.g. the server's per-socket rate
  // limiter), the server keeps broadcasting the pre-equip look forever; suppressing
  // it unboundedly would stick the panel on an outfit the server never applied. So
  // after SUPPRESS_CAP suppressed frames we give up and adopt the next authoritative
  // frame, self-healing to authoritative within the bound (see setOutfit).
  let suppressed = 0;
  // The snapshot stream is 10Hz (ADR-0163), so a normal-RTT confirming/divergent echo lands
  // within a handful of frames; ~50 frames is ~5s - generous enough never to
  // override a real echo, small enough to self-heal a dropped Equip quickly. The
  // module is clock-free, so this is a frame/call count, not a timer.
  const SUPPRESS_CAP = 50;
  let bodyId = 400;
  let skinHue = 0;
  // The item whose hue tray the player has manually expanded (a not-yet-worn item).
  // A *worn* item's tray is always shown, so it never needs an entry here.
  let expandedItem: string | null = null;
  // The hue the collection is filtered to (null = show every owned colour), driven by
  // the filter bar (ADR-0244). A filter that is no longer owned is dropped on the next
  // render, so a stale selection can never strand the list showing nothing.
  let filterHue: number | null = null;
  let selectedLayer: string | null = null;
  let selectedItem: string | null = null;
  let hueQuery = '';
  let hueView: 'list' | 'grid' = 'list';
  let open = false;
  let reserved = false;
  let favorites: (WardrobePair[] | null)[] = [];
  let favoritesInitialized = false;
  let newlyUnlockedSlot: number | null = null;
  let gold = 0;
  const confirmationPrompt = createPromptCard(doc);
  confirmationPrompt.root.classList.add('wardrobe-purchase-gump');
  confirmationPrompt.root.setAttribute('role', 'alertdialog');

  const closeConfirmation = (): void => {
    confirmationPrompt.hide();
  };

  const showConfirmation = (spec: {
    title: string;
    copy: string;
    confirmLabel: string;
    confirmClass: string;
    onConfirm: () => void;
  }): void => {
    const dialog = confirmationPrompt.root;
    dialog.replaceChildren();
    dialog.setAttribute('aria-labelledby', 'wardrobe-purchase-title');
    const title = doc.createElement('h3');
    title.id = 'wardrobe-purchase-title';
    title.textContent = spec.title;
    const copy = doc.createElement('p');
    copy.textContent = spec.copy;
    const actions = doc.createElement('div');
    actions.className = 'wardrobe-purchase-actions';
    const cancel = createPanelButton(doc, 'Cancel', 'plain');
    cancel.addEventListener('click', closeConfirmation);
    const confirm = createPanelButton(doc, spec.confirmLabel, 'primary');
    confirm.className = spec.confirmClass;
    confirm.addEventListener('click', () => {
      closeConfirmation();
      spec.onConfirm();
    });
    actions.append(cancel, confirm);
    dialog.append(title, copy, actions);
    confirmationPrompt.show();
    confirm.focus();
  };
  confirmationPrompt.root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeConfirmation();
  });

  const confirmFavoritePurchase = (): void => showConfirmation({
    title: 'Unlock outfit slot?',
    copy: 'Purchase the next favorite outfit slot for 5,000 gp?',
    confirmLabel: 'Unlock for 5,000 gp',
    confirmClass: 'wardrobe-purchase-confirm',
    onConfirm: () => opts.onUnlockFavorite?.(),
  });

  const confirmFavoriteReplace = (slot: number): void => {
    const replacement = outfit.map((pair) => ({ ...pair }));
    showConfirmation({
      title: `Replace outfit ${slot + 1}?`,
      copy: 'This will overwrite the saved outfit in this slot.',
      confirmLabel: 'Replace outfit',
      confirmClass: 'wardrobe-replace-confirm',
      onConfirm: () => opts.onSaveFavorite?.(slot, replacement),
    });
  };

  const appendFavoritePreview = (parent: HTMLElement, favorite: readonly WardrobePair[]): void => {
    const preview = doc.createElement('div');
    preview.className = 'wardrobe-favorite-preview';
    preview.setAttribute('aria-label', 'Outfit preview');
    if (opts.renderFavoritePreview) {
      opts.renderFavoritePreview(preview, {
        body: bodyId,
        bodyHue: skinHue,
        equipment: favorite.map((pair) => ({ id: pair.item, hue: pair.hue })),
      });
      parent.appendChild(preview);
      return;
    }
    for (const pair of favorite) {
      const piece = doc.createElement('span');
      piece.className = 'wardrobe-favorite-preview-piece';
      piece.style.background = hueGradientCss(pair.hue);
      piece.title = `${itemLabel(pair.item)}, hue ${pair.hue}`;
      piece.textContent = itemLabel(pair.item);
      preview.appendChild(piece);
    }
    parent.appendChild(preview);
  };

  const favoriteLock = (): SVGSVGElement => {
    const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('wardrobe-favorite-lock');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M7 10V7a5 5 0 0 1 10 0v3h1.5A1.5 1.5 0 0 1 20 11.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 20.5v-9A1.5 1.5 0 0 1 5.5 10H7zm2 0h6V7a3 3 0 0 0-6 0v3z');
    svg.appendChild(path);
    return svg;
  };

  const renderFavorites = (): void => {
    if (!favoritesEl) return;
    favoritesEl.replaceChildren();
    for (let slot = 0; slot < 8; slot++) {
      const cell = doc.createElement('div');
      cell.className = 'wardrobe-favorite';
      if (slot === newlyUnlockedSlot) cell.classList.add('wardrobe-favorite-unlocked');
      const favorite = favorites[slot];
      if (!reserved) {
        cell.classList.add('wardrobe-favorite-locked');
        const lock = favoriteLock();
        cell.append(lock, doc.createTextNode(slot === 0 ? ' Reserve to unlock' : ` Slot ${slot + 1}`));
      } else if (slot >= favorites.length) {
        cell.classList.add('wardrobe-favorite-locked');
        const unlock = doc.createElement('button');
        unlock.type = 'button';
        const next = slot === favorites.length;
        const affordable = gold >= 5_000;
        unlock.textContent = next
          ? affordable ? 'Unlock\n5,000 gp' : 'Need\n5,000 gp'
          : `Slot ${slot + 1}`;
        unlock.disabled = !next || !affordable;
        if (next && !affordable) unlock.title = `You need ${Math.max(0, 5_000 - gold).toLocaleString()} more gold`;
        const lock = favoriteLock();
        unlock.prepend(lock, doc.createElement('br'));
        if (!unlock.disabled) unlock.addEventListener('click', confirmFavoritePurchase);
        cell.appendChild(unlock);
      } else if (favorite == null) {
        const save = doc.createElement('button');
        save.type = 'button';
        save.textContent = `Save outfit ${slot + 1}`;
        save.addEventListener('click', () => opts.onSaveFavorite?.(slot, outfit.map((p) => ({ ...p }))));
        cell.appendChild(save);
      } else {
        const recall = doc.createElement('button');
        recall.type = 'button';
        recall.className = 'wardrobe-favorite-recall';
        recall.textContent = `Outfit ${slot + 1}`;
        recall.title = 'Wear this favorite outfit';
        appendFavoritePreview(recall, favorite);
        recall.addEventListener('click', () => commit(favorite.map((p) => ({ ...p }))));
        const separator = doc.createElement('div');
        separator.className = 'wardrobe-favorite-replace-separator';
        const save = doc.createElement('button');
        save.type = 'button';
        save.className = 'wardrobe-favorite-save';
        save.textContent = 'Replace';
        save.addEventListener('click', () => confirmFavoriteReplace(slot));
        cell.append(recall, separator, save);
      }
      favoritesEl.appendChild(cell);
    }
    newlyUnlockedSlot = null;
  };

  /** Order-independent key for an outfit (compares the *set* of worn pairs). */
  const outfitKey = (list: readonly WardrobePair[]): string =>
    list.map(pairKey).sort().join('|');

  const currentLook = (): WardrobeLook => ({
    body: bodyId,
    bodyHue: skinHue,
    equipment: outfit.map((p) => ({ id: p.item, hue: p.hue })),
  });

  /** The worn pair occupying each render layer, keyed by layer id. */
  const outfitByLayer = (): Map<string, WardrobePair> => {
    const map = new Map<string, WardrobePair>();
    for (const pair of outfit) {
      if (!equipment.has(pair.item)) continue;
      map.set(equipment.get(pair.item).layer, pair);
    }
    return map;
  };

  /** Push the desired outfit out (server intent + optimistic preview), then re-render. */
  const commit = (next: WardrobePair[]): void => {
    staleKey = outfitKey(outfit);
    outfit = next;
    pendingKey = outfitKey(next);
    suppressed = 0;
    opts.onEquip(next.map((p) => ({ item: p.item, hue: p.hue })));
    opts.onPreview?.(currentLook());
    render();
  };

  /** Equip `{item, hue}` into its layer, replacing whatever that layer wore. */
  const equipPair = (item: string, hue: number): void => {
    if (!equipment.has(item)) return;
    const layer = equipment.get(item).layer;
    const next = outfit.filter((p) => equipment.has(p.item) && equipment.get(p.item).layer !== layer);
    next.push({ item, hue });
    expandedItem = null; // the item is worn now, so its tray shows without manual expand
    commit(next);
  };

  /** Strip an item's whole layer back to bare (the "click an equipped item" gesture). */
  const unequipLayer = (item: string): void => {
    if (!equipment.has(item)) return;
    const layer = equipment.get(item).layer;
    commit(outfit.filter((p) => equipment.has(p.item) && equipment.get(p.item).layer !== layer));
  };

  /**
   * The owned, currently-wearable hues grouped by item id — exactly the set the
   * grouped view renders (unknown/unbaked and body-gated items skipped), so Random
   * can only ever pick a pair the player actually owns and could equip by hand.
   */
  const wearableHuesByItem = (): Map<string, number[]> => {
    const huesByItem = new Map<string, number[]>();
    for (const pair of collection) {
      if (!equipment.has(pair.item)) continue;
      const def = equipment.get(pair.item);
      if (def.bodies && !def.bodies.includes(bodyId)) continue; // body-gated: not wearable
      const hues = huesByItem.get(pair.item) ?? [];
      if (!hues.includes(pair.hue)) hues.push(pair.hue);
      huesByItem.set(pair.item, hues);
    }
    return huesByItem;
  };

  /**
   * Roll a fresh outfit from the owned collection: for each layer that has any
   * wearable item, roll either one random owned pair (hue randomized among that
   * item's owned hues) **or leave the layer empty**, then commit it through the
   * normal equip path. Only ever selects pairs the player owns; a no-op if they own
   * nothing wearable (the button then does nothing harmful).
   *
   * **Empty is one of the outcomes per layer (ADR-0171):** "no item" is treated as
   * one extra candidate alongside the owned items, so a layer with `N` owned items
   * rolls empty with probability `1/(N+1)` — slots sometimes come up unequipped for
   * variety, while owned items stay the common outcome. The empty draw is part of the
   * **same `rand` stream** (injected in tests, so a fixed seed reproduces the same
   * outfit) — never a separate unseeded source.
   */
  const randomizeOutfit = (): void => {
    const huesByItem = wearableHuesByItem();
    if (huesByItem.size === 0) return; // owns nothing wearable: harmless no-op
    const pick = <T>(arr: T[]): T => arr[Math.floor(rand() * arr.length)]!;
    const next: WardrobePair[] = [];
    for (const layer of LAYER_ORDER) {
      const items = equipment
        .all()
        .filter((def) => def.layer === layer && huesByItem.has(def.id))
        .map((def) => def.id);
      if (items.length === 0) continue;
      // Draw over the owned items **plus one empty slot**: an index of `items.length`
      // is out of range and reads as `undefined`, meaning "leave this layer bare".
      const item = items[Math.floor(rand() * (items.length + 1))];
      if (item === undefined) continue; // rolled empty: this layer stays unequipped
      next.push({ item, hue: pick(huesByItem.get(item)!) });
    }
    commit(next);
  };

  /** Bare the body: commit the empty outfit. A no-op when already bare. */
  const clearOutfit = (): void => {
    if (outfit.length === 0) return; // already bare: nothing to report
    commit([]);
  };

  /**
   * Render the grouped collection: layer -> owned item types (count badge) ->
   * unlocked hue swatches. Built fresh from `collection`/`outfit` each call with
   * `createElement`/`textContent` only (never innerHTML), so an item id can never
   * inject markup. Worn item/hue are marked; a worn item's tray is auto-open.
   */
  function render(): void {
    renderFavorites();
    if (layersColumn && itemsColumn && huesColumn) {
      renderColumns();
      return;
    }
    // Capture a scroll ANCHOR before the rebuild: the topmost item row still touching
    // the viewport's top edge, plus its offset from that edge. Restoring the anchor
    // (rather than the raw `scrollTop`) is what keeps the row you are looking at pinned
    // when a row *above* it changes height — e.g. a same-layer hue tray collapsing the
    // instant you equip from a different item in that layer. That height change is
    // exactly the reported "content shifts up/down" bug: `replaceChildren` preserves the
    // raw `scrollTop`, so the same pixel offset now frames different content. Anchoring
    // compensates. A filter change overrides this to the top instead (see setFilter).
    const prevScroll = body.scrollTop;
    const anchor = captureScrollAnchor();
    body.replaceChildren();

    // Group owned hues by item id, preserving unlock order within each item (the same
    // wearable set Random rolls from). Unaffected by the active filter — Random and the
    // filter bar both read the whole owned set, not the filtered view.
    const allHues = wearableHuesByItem();

    if (allHues.size === 0) {
      if (filterBar) filterBar.replaceChildren();
      const empty = doc.createElement('p');
      empty.className = 'wardrobe-empty';
      empty.textContent = 'You own no cosmetics yet — stay online to earn them.';
      body.appendChild(empty);
      return;
    }

    // The distinct owned hues (ascending) drive the filter bar; drop a filter that is no
    // longer owned so a stale selection can never leave the list empty.
    const ownedHues = [...new Set([...allHues.values()].flat())].sort((a, b) => a - b);
    if (filterHue !== null && !ownedHues.includes(filterHue)) filterHue = null;
    renderFilter(ownedHues);

    // Apply the hue filter: each item's tray narrows to the selected hue, and an item
    // owning none of it drops out entirely — "show me everything I own in this colour".
    const huesByItem = new Map<string, number[]>();
    for (const [item, hues] of allHues) {
      const shown = filterHue === null ? hues : hues.filter((h) => h === filterHue);
      if (shown.length > 0) huesByItem.set(item, shown);
    }

    const worn = outfitByLayer();

    for (const layer of LAYER_ORDER) {
      // Owned items in this layer, in registry order (stable, independent of unlock order).
      const items = equipment
        .all()
        .filter((def) => def.layer === layer && huesByItem.has(def.id))
        .map((def) => def.id);
      if (items.length === 0) continue;

      const section = doc.createElement('section');
      section.className = 'wardrobe-layer';
      const heading = doc.createElement('h3');
      heading.textContent = layerLabel(layer);
      section.appendChild(heading);

      const wornPair = worn.get(layer);

      for (const item of items) {
        const hues = huesByItem.get(item)!;
        const isWorn = wornPair?.item === item;
        // Under an active hue filter, every shown item auto-opens its (single-swatch)
        // tray so the filtered colour is one click from worn — the point of the filter.
        const isExpanded = isWorn || expandedItem === item || filterHue !== null;

        const row = doc.createElement('div');
        row.className = 'wardrobe-item';
        row.dataset.key = item; // stable id for the scroll anchor (captureScrollAnchor)
        if (isWorn) row.classList.add('worn');

        const header = doc.createElement('button');
        header.type = 'button';
        header.className = 'wardrobe-item-header';
        header.setAttribute('aria-expanded', String(isExpanded));
        header.setAttribute('aria-pressed', String(isWorn));
        const name = doc.createElement('span');
        name.className = 'wardrobe-item-name';
        name.textContent = itemLabel(item);
        const count = doc.createElement('span');
        count.className = 'wardrobe-item-count';
        count.textContent = `×${hues.length}`;
        header.append(name, count);
        header.addEventListener('click', () => {
          if (worn.get(layer)?.item === item) {
            // A worn item clicked again: unequip its layer to bare.
            unequipLayer(item);
          } else {
            // A not-worn item: toggle its hue tray so the player can pick a hue.
            expandedItem = expandedItem === item ? null : item;
            render();
          }
        });
        row.appendChild(header);

        if (isExpanded) {
          const tray = doc.createElement('div');
          tray.className = 'wardrobe-hues hues';
          for (const hue of hues) {
            const swatch = doc.createElement('button');
            swatch.type = 'button';
            swatch.dataset.item = item;
            swatch.dataset.hue = String(hue);
            swatch.style.background = hueGradientCss(hue);
            swatch.title = `Hue ${hue}`;
            swatch.setAttribute('aria-label', `${itemLabel(item)} hue ${hue}`);
            const hueWorn = isWorn && wornPair!.hue === hue;
            swatch.setAttribute('aria-pressed', String(hueWorn));
            swatch.addEventListener('click', () => equipPair(item, hue));
            tray.appendChild(swatch);
          }
          row.appendChild(tray);
        }

        section.appendChild(row);
      }

      body.appendChild(section);
    }

    // Restore the captured anchor (see the capture note above).
    restoreScrollAnchor(anchor, prevScroll);
  }

  /**
   * The topmost `.wardrobe-item` still touching the scroll container's top edge, and how
   * far its top sits from that edge, for {@link restoreScrollAnchor}. `null` when the
   * list is empty or unlaid-out (jsdom reports zero rects, so this returns `null` and the
   * restore falls back to the raw scroll position — headless tests still pass).
   */
  function captureScrollAnchor(): { key: string; top: number } | null {
    const edge = body.getBoundingClientRect().top;
    for (const el of body.querySelectorAll<HTMLElement>('.wardrobe-item')) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > edge && el.dataset.key) return { key: el.dataset.key, top: rect.top - edge };
    }
    return null;
  }

  /**
   * Re-pin the anchor row to the same offset from the top edge after a rebuild, so the
   * content the player was looking at does not jump when rows above it change height.
   * Falls back to the raw pre-rebuild `scrollTop` when there is no anchor or the anchored
   * item is gone (e.g. filtered out) — never worse than the old behaviour.
   */
  function restoreScrollAnchor(anchor: { key: string; top: number } | null, prevScroll: number): void {
    if (anchor) {
      const el = body.querySelector<HTMLElement>(`.wardrobe-item[data-key="${anchor.key}"]`);
      if (el) {
        const now = el.getBoundingClientRect().top - body.getBoundingClientRect().top;
        body.scrollTop += now - anchor.top;
        return;
      }
    }
    body.scrollTop = prevScroll;
  }

  /**
   * Populate the hue filter bar (ADR-0244) from the distinct owned hues: an "All" chip
   * that clears the filter, then one swatch per owned colour. Rebuilt on every render so
   * the pressed state tracks `filterHue`. Left empty (and CSS-collapsed) below two owned
   * hues, where there is nothing to filter by. No-op when the bar is absent (headless).
   */
  function renderFilter(ownedHues: number[]): void {
    if (!filterBar) return;
    filterBar.replaceChildren();
    if (ownedHues.length < 2) return; // one colour (or none): nothing to filter by

    const label = doc.createElement('span');
    label.className = 'wardrobe-filter-label';
    label.textContent = 'Hue';
    filterBar.appendChild(label);

    const all = doc.createElement('button');
    all.type = 'button';
    all.className = 'wardrobe-filter-all';
    all.textContent = 'All';
    all.title = 'Show every colour';
    all.setAttribute('aria-pressed', String(filterHue === null));
    all.addEventListener('click', () => setFilter(null));
    filterBar.appendChild(all);

    for (const hue of ownedHues) {
      const swatch = doc.createElement('button');
      swatch.type = 'button';
      swatch.className = 'wardrobe-filter-swatch';
      swatch.dataset.hue = String(hue);
      swatch.style.background = hueGradientCss(hue);
      swatch.title = `Show only hue ${hue}`;
      swatch.setAttribute('aria-label', `Filter by hue ${hue}`);
      swatch.setAttribute('aria-pressed', String(filterHue === hue));
      // Clicking the active hue toggles the filter back off; any other hue selects it.
      swatch.addEventListener('click', () => setFilter(filterHue === hue ? null : hue));
      filterBar.appendChild(swatch);
    }
  }

  /** Render the full-gump layer -> item -> hue browser. */
  function renderColumns(): void {
    layersColumn!.replaceChildren();
    itemsColumn!.replaceChildren();
    huesColumn!.replaceChildren();
    const allHues = wearableHuesByItem();
    const visibleHues = new Map<string, number[]>();
    for (const [item, hues] of allHues) {
      const shown = hues.filter((hue) => {
        if (filterHue !== null && hue !== filterHue) return false;
        return hueQuery === '' || String(hue).includes(hueQuery);
      });
      if (shown.length > 0) visibleHues.set(item, shown);
    }
    const availableLayers = LAYER_ORDER.filter((layer) =>
      equipment.all().some((def) => def.layer === layer && visibleHues.has(def.id)),
    );
    if (!selectedLayer || !availableLayers.includes(selectedLayer)) selectedLayer = availableLayers[0] ?? null;

    for (const layer of availableLayers) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'wardrobe-choice wardrobe-layer-choice';
      button.textContent = layerLabel(layer);
      button.setAttribute('aria-pressed', String(layer === selectedLayer));
      button.addEventListener('click', () => {
        selectedLayer = layer;
        selectedItem = null;
        render();
      });
      layersColumn!.appendChild(button);
    }

    const items = selectedLayer
      ? equipment.all().filter((def) => def.layer === selectedLayer && visibleHues.has(def.id))
      : [];
    if (!selectedItem || !items.some((def) => def.id === selectedItem)) selectedItem = items[0]?.id ?? null;
    const worn = outfitByLayer();
    for (const def of items) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'wardrobe-choice wardrobe-item-choice';
      button.dataset.item = def.id;
      button.setAttribute('aria-pressed', String(def.id === selectedItem));
      if (worn.get(def.layer)?.item === def.id) button.classList.add('worn');
      const name = doc.createElement('span');
      name.textContent = itemLabel(def.id);
      const count = doc.createElement('span');
      count.className = 'wardrobe-item-count';
      count.textContent = String(visibleHues.get(def.id)!.length);
      button.append(name, count);
      button.addEventListener('click', () => {
        selectedItem = def.id;
        render();
      });
      itemsColumn!.appendChild(button);
    }

    const ownedHues = [...new Set([...allHues.values()].flat())].sort((a, b) => a - b);
    renderHueSelect(ownedHues);
    const shownHues = selectedItem ? visibleHues.get(selectedItem) ?? [] : [];
    for (const hue of shownHues) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'wardrobe-hue-choice';
      button.dataset.hue = String(hue);
      button.style.background = hueGradientCss(hue);
      // A worn hue is a pressed toggle: clicking it again takes that item off
      // (unequips its layer to bare), so a single piece can be removed without
      // clearing the whole outfit. Any other hue swaps to it as before.
      const isWornHue = worn.get(selectedLayer!)?.item === selectedItem && worn.get(selectedLayer!)?.hue === hue;
      button.setAttribute('aria-label', isWornHue ? `${itemLabel(selectedItem!)} hue ${hue}, worn — take off` : `${itemLabel(selectedItem!)} hue ${hue}`);
      button.setAttribute('aria-pressed', String(isWornHue));
      button.title = isWornHue ? 'Take off' : `Hue ${hue}`;
      const label = doc.createElement('span');
      label.textContent = `Hue ${hue}`;
      button.appendChild(label);
      button.addEventListener('click', () => (isWornHue ? unequipLayer(selectedItem!) : equipPair(selectedItem!, hue)));
      huesColumn!.appendChild(button);
    }
    if (shownHues.length === 0) {
      const empty = doc.createElement('p');
      empty.className = 'wardrobe-empty';
      empty.textContent = allHues.size > 0 && visibleHues.size === 0
        ? 'No cosmetics match this hue search.'
        : 'Select an item to see its hues.';
      huesColumn!.appendChild(empty);
    }
  }

  function renderHueSelect(ownedHues: number[]): void {
    if (!filterSelect) return;
    filterSelect.textContent = filterHue === null ? 'All owned hues' : `Hue ${filterHue}`;
    if (!filterOptions) return;
    filterOptions.replaceChildren();
    const addOption = (hue: number | null): void => {
      const option = doc.createElement('button');
      option.type = 'button';
      option.className = 'wardrobe-hue-filter-option';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-pressed', String(filterHue === hue));
      if (hue !== null) option.style.background = hueGradientCss(hue);
      const label = doc.createElement('span');
      label.textContent = hue === null ? 'All owned hues' : `Hue ${hue}`;
      option.appendChild(label);
      option.addEventListener('click', () => {
        filterHue = hue;
        if (filterMenu) filterMenu.open = false;
        render();
      });
      filterOptions.appendChild(option);
    };
    addOption(null);
    for (const hue of ownedHues) {
      addOption(hue);
    }
  }

  /** Apply a hue filter (or clear it with `null`) and re-list from the top. */
  function setFilter(hue: number | null): void {
    if (hue === filterHue) return;
    filterHue = hue;
    render();
    body.scrollTop = 0; // a filter change re-lists; a fresh top read is least surprising
  }

  const window = createUiWindow({
    doc,
    id: 'wardrobe',
    title: 'Wardrobe',
    ariaLabel: 'Wardrobe',
    fallbackWidth: 1040,
    fallbackHeight: 680,
    escapeCloses: true,
    onOpen: () => {
      open = true;
      button?.setAttribute('aria-expanded', 'true');
      opts.onOpenChange?.(true);
      render();
      opts.onPreview?.(currentLook());
      opts.onOpen?.();
    },
    onClose: () => {
      open = false;
      button?.setAttribute('aria-expanded', 'false');
      opts.onOpenChange?.(false);
      opts.onClose?.();
    },
  });

  const setOpen = (next: boolean): void => {
    if (next === open) return;
    if (window) {
      if (next) window.open();
      else window.close();
    } else {
      open = next;
      panel.hidden = !open;
      button?.setAttribute('aria-expanded', String(open));
      opts.onOpenChange?.(open);
      if (open) {
        render();
        opts.onPreview?.(currentLook());
        opts.onOpen?.();
      } else {
        opts.onClose?.();
      }
    }
  };

  button?.addEventListener('click', () => setOpen(!open));
  if (!window) closeBtn?.addEventListener('click', () => setOpen(false));
  // A user toggle reports the new state up to the server (ADR-0123). The programmatic
  // `setAutoEquip` sets `.checked` directly without dispatching, so it never lands here.
  autoEquipBox?.addEventListener('change', () => opts.onAutoEquip?.(autoEquipBox.checked));
  // The outfit-action controls commit through the same equip path as an item click.
  randomBtn?.addEventListener('click', () => randomizeOutfit());
  clearBtn?.addEventListener('click', () => clearOutfit());
  searchInput?.addEventListener('input', () => {
    hueQuery = searchInput.value.trim();
    render();
  });
  const setHueView = (view: 'list' | 'grid'): void => {
    hueView = view;
    huesColumn?.classList.toggle('grid-view', view === 'grid');
    huesListView?.setAttribute('aria-pressed', String(view === 'list'));
    huesGridView?.setAttribute('aria-pressed', String(view === 'grid'));
  };
  huesListView?.addEventListener('click', () => setHueView('list'));
  huesGridView?.addEventListener('click', () => setHueView('grid'));
  setHueView(hueView);
  // Escape closes the panel, matching the chat settings popover and board window.
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open) setOpen(false);
  });

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!open),
    isOpen: () => open,
    setCollection(unlocked) {
      collection = unlocked.map((p) => ({ item: p.item, hue: p.hue }));
      if (open) render();
    },
    setOutfit(next) {
      const incoming = outfitKey(next);
      if (incoming === outfitKey(outfit)) {
        // Authoritative state already matches what we show (a confirming echo or the
        // steady stream): clear any pending suppression and don't thrash the panel.
        pendingKey = null;
        staleKey = null;
        suppressed = 0;
        return;
      }
      // While an optimistic equip is pending, suppress the in-flight frame still
      // carrying the pre-equip look (incoming === staleKey): adopting it would revert
      // the panel/preview before the server applied our Equip. Any other authoritative
      // outfit - the confirming echo or a server-side divergence (e.g. a 5-min unlock
      // boundary auto-equipping a freshly rolled item) - is adopted and clears the
      // suppression. But suppression is *bounded*: if our Equip was silently dropped
      // (rate limiter), the server broadcasts the pre-equip look forever, so after
      // SUPPRESS_CAP suppressed frames we give up and adopt it, self-healing to
      // authoritative within the bound instead of sticking on a never-applied outfit.
      if (pendingKey !== null && incoming === staleKey && suppressed < SUPPRESS_CAP) {
        suppressed += 1;
        return;
      }
      pendingKey = null;
      staleKey = null;
      suppressed = 0;
      outfit = next.map((p) => ({ item: p.item, hue: p.hue }));
      if (open) {
        render();
        opts.onPreview?.(currentLook());
      }
    },
    setBody(body, bodyHue) {
      if (body === bodyId && bodyHue === skinHue) return; // unchanged: don't thrash
      bodyId = body;
      skinHue = bodyHue;
      if (open) {
        render();
        opts.onPreview?.(currentLook());
      }
    },
    setAutoEquip(enabled) {
      // Reflect the authoritative preference into the box without dispatching a
      // `change`, so restoring a saved choice never echoes back as a fresh toggle.
      if (autoEquipBox) autoEquipBox.checked = enabled;
    },
    setFavorites(nextReserved, nextFavorites) {
      const previousCount = favorites.length;
      reserved = nextReserved;
      favorites = nextFavorites.map((favorite) => favorite?.map((pair) => ({ ...pair })) ?? null);
      newlyUnlockedSlot = favoritesInitialized && favorites.length > previousCount
        ? favorites.length - 1
        : null;
      if (newlyUnlockedSlot !== null) opts.onFavoriteUnlocked?.();
      favoritesInitialized = true;
      renderFavorites();
    },
    setGold(nextGold) {
      gold = Math.max(0, nextGold);
      renderFavorites();
    },
  };
}
